import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Activity, Wallet, TrendingUp } from 'lucide-react';
import './App.css';
import './index.css';

// 1. 우리가 만든 부품들 가져오기
import { Navbar } from './components/Navbar';
import { PriceChart } from './components/PriceChart';
import { useTheme } from './hooks/useTheme';
import { useCryptoData } from './hooks/useCryptoData';

function App() {
  const [targetCoin, setTargetCoin] = useState('KRW-BTC');
  
  // 2. 훅(Logic) 실행
  const { isDarkMode, toggleTheme } = useTheme();
  const { priceData, status, currentPrice, marketStats } = useCryptoData(targetCoin);

  // 화폐 단위 포맷팅 함수
  const formatPrice = (price) => {
    if (!price) return "Loading...";
    return new Intl.NumberFormat('ko-KR', { style: 'currency', currency: 'KRW' }).format(price);
  };
  const formatNumber = (num) => new Intl.NumberFormat('ko-KR').format(num || 0);

  // 3. 화면 렌더링 (Layout)
  return (
    <div className={`min-h-screen transition-colors duration-500 font-sans 
      ${isDarkMode 
        ? 'bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-gray-700 via-gray-900 to-black text-white' 
        : 'bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-rose-200 via-red-100 to-white text-slate-900'
      }`
    }>
      {/* 헤더 메뉴 배치 */}
      <Navbar isDarkMode={isDarkMode} toggleTheme={toggleTheme} status={status} />

      <main className="pt-24 pb-10 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        
        {/* 코인 선택 버튼 */}
        <div className="flex space-x-2 mb-8 overflow-x-auto pb-2 scrollbar-hide">
          {['KRW-BTC', 'KRW-ETH', 'KRW-XRP', 'KRW-SOL'].map((coin) => (
            <button
              key={coin}
              onClick={() => setTargetCoin(coin)}
              className={`px-6 py-2.5 rounded-xl text-sm font-bold transition-all duration-300 border backdrop-blur-sm whitespace-nowrap
                ${targetCoin === coin
                  ? 'bg-blue-600/90 text-white border-blue-500 shadow-lg shadow-blue-500/25 scale-105'
                  : 'bg-white/5 dark:bg-white/5 border-gray-200 dark:border-white/10 text-gray-500 dark:text-gray-400 hover:bg-white/20'
                }`}
            >
              {coin.replace('KRW-', '')}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          
          {/* 차트 영역 (3/4 차지) */}
          <div className="lg:col-span-3">
            <div className="relative p-6 rounded-3xl bg-white/40 dark:bg-black/40 border border-white/50 dark:border-white/5 backdrop-blur-xl shadow-2xl">
              <div className="flex justify-between items-end mb-6">
                <div>
                  <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Current Price</h2>
                  <AnimatePresence mode='wait'>
                    <motion.div
                      key={currentPrice}
                      initial={{ y: 10, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      exit={{ y: -10, opacity: 0 }}
                      className="text-4xl sm:text-5xl font-extrabold tracking-tight"
                    >
                      {formatPrice(currentPrice)}
                    </motion.div>
                  </AnimatePresence>
                </div>
                <div className="flex items-center gap-2 text-green-500 bg-green-500/10 px-3 py-1.5 rounded-lg">
                  <TrendingUp size={20} />
                  <span className="font-bold text-sm">Live</span>
                </div>
              </div>
              
              {/* 차트 컴포넌트 배치 */}
              <PriceChart 
                data={priceData[targetCoin]} 
                isDarkMode={isDarkMode}
                targetCoin={targetCoin} 
              />
            </div>
          </div>

          {/* 사이드바 통계 영역 (1/4 차지) */}
          <div className="space-y-6">
            <div className="p-5 rounded-3xl bg-white/60 dark:bg-gray-800/40 border border-white/50 dark:border-white/5 backdrop-blur-md shadow-lg">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg text-blue-600 dark:text-blue-400">
                  <Activity size={20} />
                </div>
                <h3 className="font-bold text-lg">24h Market</h3>
              </div>
              <div className="space-y-4">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">High</span>
                  <span className="font-mono font-medium text-red-500">{formatPrice(marketStats.high)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">Low</span>
                  <span className="font-mono font-medium text-blue-500">{formatPrice(marketStats.low)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">Volume</span>
                  <span className="font-mono font-medium">
                    {formatNumber(marketStats.volume)}
                    <span className="text-xs ml-1 text-gray-500">{targetCoin.split('-')[1]}</span>
                  </span>
                </div>
              </div>
            </div>

            {/* Wallet 더미 카드 */}
            <div className="p-5 rounded-3xl bg-gradient-to-br from-purple-500 to-indigo-600 text-white shadow-xl">
              <div className="flex items-center gap-3 mb-4">
                <Wallet size={20} className="text-purple-200" />
                <h3 className="font-bold text-lg">My Wallet</h3>
              </div>
              <p className="text-purple-100 text-sm mb-4">Connect wallet to trade.</p>
              <button className="w-full py-2 bg-white/20 hover:bg-white/30 rounded-lg font-semibold transition-colors text-sm backdrop-blur-sm">Connect</button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default App;